/**
 * Imported immediately after polyfills, before telemetry or auth can read the
 * URL. Clu's Teams links arrive as `#teams_connect=…` (an admin connecting a
 * Microsoft organization) or `#teams_link=…` (a person linking their Teams
 * account). The fragment never reaches the CDN; a query copy is scrubbed but
 * not trusted, as with Slack's account link.
 *
 * The signed state moves to sessionStorage only after the address bar is
 * clean, so it survives sign-in pages that leave this document: Google or
 * Microsoft sign-in, and app.clugtm.com, a Clerk satellite that signs in on
 * app.cleanlist.ai and comes back. It is single-use and expires in a day.
 * Keep this module free of app imports so capture precedes initialization.
 */
export type TeamsCluHandoffKind = 'connect' | 'link';

const PARAMS: Record<TeamsCluHandoffKind, string> = {
  connect: 'teams_connect',
  link: 'teams_link',
};
const STORAGE_KEY = 'cleanlist:teams-clu-handoff';

type Pending = Partial<Record<TeamsCluHandoffKind, string>>;
let memory: Pending = {};
const listeners = new Set<() => void>();

function read(): Pending {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return memory;
    const value = JSON.parse(raw) as Record<string, unknown>;
    return {
      connect: typeof value.connect === 'string' ? value.connect : undefined,
      link: typeof value.link === 'string' ? value.link : undefined,
    };
  } catch {
    return memory;
  }
}

function write(value: Pending): void {
  memory = Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry)
  ) as Pending;
  try {
    if (Object.keys(memory).length === 0)
      window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
  } catch {
    // Storage blocked: the same-page signed-in flow still works from memory.
  }
  listeners.forEach((listener) => listener());
}

export function captureTeamsCluConnectState(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const hash = new URLSearchParams(url.hash.replace(/^#\??/, ''));
  const names = Object.values(PARAMS);
  const inQuery = names.some((name) => url.searchParams.has(name));
  const inHash = names.some((name) => hash.has(name));
  if (!inQuery && !inHash) return;

  const captured: Pending = {};
  for (const [kind, name] of Object.entries(PARAMS) as [
    TeamsCluHandoffKind,
    string,
  ][]) {
    const value = hash.get(name);
    if (value) captured[kind] = value;
    url.searchParams.delete(name);
    hash.delete(name);
  }
  if (inHash) url.hash = hash.toString() ? `#${hash.toString()}` : '';
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  );
  // Storage only once the credential-bearing location is gone.
  if (Object.keys(captured).length) write({ ...read(), ...captured });
}

export function getPendingTeamsCluHandoff(
  kind: TeamsCluHandoffKind
): string | null {
  return read()[kind] ?? null;
}

export function clearPendingTeamsCluHandoff(
  kind: TeamsCluHandoffKind,
  expectedState: string
): void {
  const pending = read();
  if (pending[kind] !== expectedState) return;
  write({ ...pending, [kind]: undefined });
}

export const getPendingTeamsCluConnectState = () =>
  getPendingTeamsCluHandoff('connect');
export const clearPendingTeamsCluConnectState = (expectedState: string) =>
  clearPendingTeamsCluHandoff('connect', expectedState);

export function subscribeTeamsCluConnectState(
  listener: () => void
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

captureTeamsCluConnectState();
